import React from 'react';
import { Clock, AlertCircle, CheckCircle, Users } from 'lucide-react';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

// Plan name mapping to match withnimble.com pricing
const PLAN_DISPLAY_NAMES = {
  'basic': 'Basic Plan',
  'premium': 'Premium Plan', 
  'business': 'Business Plan',
  'pro': 'Pro Plan',
  'admin_reserved': 'Admin Reserved',
  'unassigned': 'Unassigned',
  'Basic Plan': 'Basic Plan',
  'Premium Plan': 'Premium Plan',
  'Business Plan': 'Business Plan',
  'Pro Plan': 'Pro Plan',
  'Admin Reserved': 'Admin Reserved',
  'Unassigned': 'Unassigned'
};

const BandwidthBadge = ({ 
  hoursAvailable,        // Hours available for work today (reserved - used)
  dailyHours,            // Total reserved/booked hours
  hoursUsedToday = 0,
  hoursAvailableForBooking = 0, // Remaining capacity for new bookings (8 - booked)
  maxDailyCapacity = 8,  // Total VA capacity (always 8)
  planName = '',
  allocationsCount = 0,
  size = 'default', // 'compact', 'default', 'detailed'
  showProgress = true,
  className = ''
}) => {
  // Format plan name for display
  const displayPlanName = PLAN_DISPLAY_NAMES[planName] || planName || 'Unassigned';
  
  // Key metrics
  const totalCapacity = maxDailyCapacity || 8;
  const bookedHours = dailyHours || 0;
  const availableForBooking = hoursAvailableForBooking || (totalCapacity - bookedHours);
  const bookedPercent = totalCapacity > 0 ? Math.round((bookedHours / totalCapacity) * 100) : 0;
  const availablePercent = totalCapacity > 0 ? Math.round((availableForBooking / totalCapacity) * 100) : 100;
  
  // Determine status color based on available capacity for booking
  const getStatusColor = () => {
    if (bookedHours === 0) return 'gray'; // Unassigned - fully available
    if (availablePercent > 50) return 'green'; // More than 50% available
    if (availablePercent > 0) return 'yellow'; // Some capacity left
    return 'red'; // Fully booked
  };
  
  const statusColor = getStatusColor();
  
  const colorClasses = {
    green: {
      badge: 'bg-green-100 text-green-700 border-green-200',
      progress: 'bg-green-500',
      text: 'text-green-600'
    },
    yellow: {
      badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      progress: 'bg-yellow-500',
      text: 'text-yellow-600'
    },
    red: {
      badge: 'bg-red-100 text-red-700 border-red-200',
      progress: 'bg-red-500',
      text: 'text-red-600'
    },
    gray: {
      badge: 'bg-gray-100 text-gray-700 border-gray-200',
      progress: 'bg-gray-400',
      text: 'text-gray-600'
    }
  };

  if (size === 'compact') {
    // Show "Fully Booked" badge when all capacity is allocated
    if (bookedHours > 0 && availableForBooking <= 0) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                className="bg-amber-100 text-amber-800 border-amber-300 cursor-help font-semibold"
                data-testid="bandwidth-badge-fully-booked"
              >
                <AlertCircle className="w-3 h-3 mr-1" />
                Fully Booked
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-sm">
                <strong>Capacity: {totalCapacity}h/day</strong>
                <br />
                <span className="text-amber-600">All {bookedHours}h are allocated</span>
                {allocationsCount > 0 && (
                  <>
                    <br />
                    <span className="text-gray-500">{allocationsCount} client(s)</span>
                  </>
                )}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              className={`${colorClasses[statusColor].badge} cursor-help ${className}`}
              data-testid="bandwidth-badge-compact"
            >
              <Clock className="w-3 h-3 mr-1" />
              {availableForBooking}h free
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="text-sm">
              <strong>Capacity: {totalCapacity}h/day</strong>
              <br />
              <span className="text-green-600">{availableForBooking}h available for booking</span>
              <br />
              <span className="text-blue-600">{bookedHours}h already booked</span>
              {allocationsCount > 0 && (
                <>
                  <br />
                  <span className="text-gray-500">{allocationsCount} client(s)</span>
                </>
              )}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (size === 'detailed') {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`} data-testid="bandwidth-badge-detailed">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Daily Bandwidth ({totalCapacity}h total)
          </span>
          {bookedHours > 0 && (
            <Badge className={colorClasses[statusColor].badge}>
              {bookedHours}h booked
            </Badge>
          )}
        </div>
        
        {showProgress && (
          <div className="space-y-2">
            <Progress 
              value={bookedPercent} 
              className="h-2" 
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>{bookedHours}h booked</span>
              <span>{availableForBooking}h available</span>
            </div>
          </div>
        )}
        
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div className="bg-white rounded p-2">
            <p className="text-lg font-bold text-gray-900">{totalCapacity}h</p>
            <p className="text-xs text-gray-500">Total/Day</p>
          </div>
          <div className="bg-white rounded p-2">
            <p className="text-lg font-bold text-blue-600">{bookedHours}h</p>
            <p className="text-xs text-gray-500">Booked</p>
          </div>
          <div className="bg-white rounded p-2">
            <p className={`text-lg font-bold ${colorClasses[statusColor].text}`}>{availableForBooking}h</p>
            <p className="text-xs text-gray-500">Available</p>
          </div>
        </div>
        
        {allocationsCount > 0 && (
          <div className="mt-2 text-center text-xs text-gray-500">
            <Users className="w-3 h-3 inline mr-1" />
            {allocationsCount} client allocation(s)
          </div>
        )}
      </div>
    );
  }

  // Default size - shows available capacity prominently
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="bandwidth-badge-default">
      <div className="flex items-center gap-1">
        {statusColor === 'green' && <CheckCircle className="w-4 h-4 text-green-500" />}
        {statusColor === 'yellow' && <AlertCircle className="w-4 h-4 text-yellow-500" />}
        {statusColor === 'red' && <AlertCircle className="w-4 h-4 text-red-500" />}
        {statusColor === 'gray' && <Clock className="w-4 h-4 text-gray-400" />}
        <span className={`text-sm font-medium ${colorClasses[statusColor].text}`}>
          {availableForBooking}h free
        </span>
        <span className="text-xs text-gray-400">
          ({bookedHours}/{totalCapacity}h booked)
        </span>
      </div>
      {showProgress && (
        <div className="w-16">
          <Progress value={bookedPercent} className="h-1.5" />
        </div>
      )}
    </div>
  );
};

export default BandwidthBadge;
